import { useEffect, useRef } from "react";
import { Trans } from "react-i18next";
import classNames from "classnames";
import { IconAttention } from "@icons/index";
import { useVideoPlayer } from "@hooks/videoplayer";

interface DetailVideoPlayerProps {
  videoURL: string;
  aspectRatio?: string;
  suspended?: boolean;
}

export const DetailVideoPlayer = ({ videoURL, aspectRatio, suspended = false }: DetailVideoPlayerProps) => {
  const isHorizontal = aspectRatio === "RATIO_16_9" || aspectRatio === "16:9";
  const shouldResumeAfterSuspendRef = useRef(false);
  const suspendedRef = useRef(suspended);

  const { refPlayer } = useVideoPlayer(
    {
      id: "detail-videoplayer",
      url: videoURL,
      ignores: ["cssFullScreen", "playbackrate"],
      download: false,
      fluid: true,
    },
    [],
    { kalo_pos: "video_detail", kalo_type: "detail" },
  );

  useEffect(() => {
    if (suspendedRef.current === suspended) return;

    const player = refPlayer.current;
    suspendedRef.current = suspended;

    if (!player) return;

    if (suspended) {
      shouldResumeAfterSuspendRef.current = !player.paused;
      player.pause();
      return;
    }

    if (shouldResumeAfterSuspendRef.current) {
      shouldResumeAfterSuspendRef.current = false;
      Promise.resolve(player.play()).catch(() => {});
    }
  }, [refPlayer, suspended]);

  return (
    <div className="w-full h-full flex flex-col items-center gap-[22px] pt-[47px]">
      <div className="flex-1 min-h-0 w-full flex items-center justify-center overflow-hidden">
        <div
          className={classNames(
            "overflow-hidden bg-black detail-video-container",
            isHorizontal ? "w-full aspect-video max-h-full" : "h-full aspect-9/16 max-w-full",
          )}
          data-detail-player-suspended={suspended ? "true" : "false"}
        >
          <div id="detail-videoplayer" />
        </div>
      </div>
      <div className="shrink-0 flex items-center gap-[4px] text-kds-fg-subtle text-[12px] font-normal">
        <IconAttention className="w-[20px] h-[20px] **:stroke-current" />
        <Trans>Kaloclip AI may make mistakes, so double-check your videos.</Trans>
      </div>
    </div>
  );
};
